import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plane, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState('');

  useEffect(() => { document.title = 'Reset password | FlightDeals.in'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.forgotPassword({ email });
      setSent(true);
      if (data?.dev_reset_link) setDevLink(data.dev_reset_link);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="text-center mb-6">
          <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground mb-3"><Plane className="h-6 w-6" /></span>
          <h1 className="font-display text-2xl font-semibold">Forgot your password?</h1>
          <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
            <div>
              <Label>Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input type="email" required className="pl-9 h-11" value={email}
                  onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" placeholder="you@example.com" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="forgot-submit-button">
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-5 text-center" data-testid="forgot-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <p className="font-medium">Check your inbox</p>
            <p className="text-sm text-muted-foreground mt-1">If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a link to reset your password. It expires in 30 minutes.</p>
            {devLink && (
              <div className="mt-4 text-left rounded-lg bg-secondary/60 p-3" data-testid="forgot-dev-link-box">
                <p className="text-xs text-muted-foreground mb-1">Developer mode (no email provider configured yet) — use this link:</p>
                <a href={devLink} className="text-accent text-xs break-all underline" data-testid="forgot-dev-link">{devLink}</a>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/login" className="text-accent inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to sign in</Link>
        </p>
      </div>
    </Layout>
  );
}
